import { useState } from 'react';
import { Box, Button, IconButton, Popover, Slider, Stack, Switch, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import GraphicEqRounded from '@mui/icons-material/GraphicEqRounded';
import { DEFAULT_SOUND, SOUND_PRESETS, SoundSettings } from '../../hooks/useSoundBoost';

type Props = {
  settings: SoundSettings;
  onChange: (patch: Partial<SoundSettings>) => void;
  onReset: () => void;
  supported?: boolean;
  sx?: SxProps<Theme>;
};

const same = (a: SoundSettings, b: SoundSettings) =>
  a.boost === b.boost && a.bass === b.bass && a.treble === b.treble && a.normalize === b.normalize;

const SoundSettingsButton = ({ settings, onChange, onReset, supported = true, sx }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const modified = !same(settings, DEFAULT_SOUND);

  return (
    <>
      <Tooltip title="Sound settings">
        <IconButton
          size="small"
          aria-label="Sound settings"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={[{ color: modified ? '#F6A938' : 'inherit' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        >
          <GraphicEqRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Above the full-screen video player dialog
        sx={{ zIndex: 2000 }}
        slotProps={{ paper: { sx: { width: 300, p: 2, borderRadius: 3 } } }}
      >
        <Stack spacing={1.5}>
          <Typography fontWeight={800}>Sound settings</Typography>
          {!supported && (
            <Typography variant="caption" color="warning.main">
              This file's server doesn't allow sound enhancement, so only the normal volume works here.
            </Typography>
          )}

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {SOUND_PRESETS.map((p) => (
              <Button
                key={p.id}
                size="small"
                disabled={!supported}
                variant={same(settings, p.values) ? 'contained' : 'outlined'}
                onClick={() => onChange(p.values)}
                sx={{ textTransform: 'none', borderRadius: 2, minWidth: 0 }}
              >
                {p.label}
              </Button>
            ))}
          </Stack>

          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Volume boost</Typography>
              <Typography variant="body2" fontWeight={700}>{Math.round(settings.boost * 100)}%</Typography>
            </Stack>
            <Slider size="small" min={1} max={10} step={0.25} value={settings.boost} disabled={!supported}
              onChange={(_, v) => onChange({ boost: v as number })} />
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Bass</Typography>
              <Typography variant="body2" fontWeight={700}>{settings.bass > 0 ? '+' : ''}{settings.bass} dB</Typography>
            </Stack>
            <Slider size="small" min={-12} max={12} step={1} value={settings.bass} disabled={!supported}
              onChange={(_, v) => onChange({ bass: v as number })} />
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Treble</Typography>
              <Typography variant="body2" fontWeight={700}>{settings.treble > 0 ? '+' : ''}{settings.treble} dB</Typography>
            </Stack>
            <Slider size="small" min={-12} max={12} step={1} value={settings.treble} disabled={!supported}
              onChange={(_, v) => onChange({ treble: v as number })} />
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2">Normalize loudness</Typography>
              <Typography variant="caption" color="text.secondary">Lifts quiet parts, tames loud ones</Typography>
            </Box>
            <Switch size="small" checked={settings.normalize} disabled={!supported}
              onChange={(e) => onChange({ normalize: e.target.checked })} />
          </Stack>

          <Button size="small" onClick={onReset} disabled={!modified} sx={{ textTransform: 'none', alignSelf: 'flex-end' }}>
            Reset
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export default SoundSettingsButton;
